import { useState, useMemo, useEffect } from 'react';
import { 
  PatientRecord, 
  KPIMetric, 
  formatCurrency, 
  getBMICategory 
} from '@/lib/index';
import { patientData } from '@/data/healthcareData';

/**
 * Interface for the dashboard filtering state
 */
export interface FilterState {
  search: string;
  region: string | 'all';
  ageGroup: string | 'all';
  bmiCategory: string | 'all';
  smoker: 'all' | 'yes' | 'no';
  treatmentType: string | 'all';
  status: string | 'all';
}

const INITIAL_FILTERS: FilterState = {
  search: '',
  region: 'all',
  ageGroup: 'all',
  bmiCategory: 'all',
  smoker: 'all',
  treatmentType: 'all',
  status: 'all',
};

/**
 * Custom hook for managing patient data filters and returning the filtered subset
 */
export const usePatientFilters = () => {
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);

  const updateFilter = (key: keyof FilterState, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => setFilters(INITIAL_FILTERS);

  const filteredData = useMemo(() => {
    return patientData.filter((patient) => {
      const matchesSearch = patient.name.toLowerCase().includes(filters.search.toLowerCase()) || 
                           patient.id.toLowerCase().includes(filters.search.toLowerCase());
      const matchesRegion = filters.region === 'all' || patient.region === filters.region;
      const matchesSmoker = filters.smoker === 'all' || patient.smoker === filters.smoker;
      const matchesTreatment = filters.treatmentType === 'all' || patient.treatmentType === filters.treatmentType;
      const matchesStatus = filters.status === 'all' || patient.status === filters.status;
      
      // Age Group Logic
      let matchesAge = true;
      if (filters.ageGroup !== 'all') {
        if (filters.ageGroup === '65+') {
          matchesAge = patient.age >= 65;
        } else {
          const [min, max] = filters.ageGroup.split('-').map(Number);
          matchesAge = patient.age >= min && patient.age <= max;
        }
      }

      // BMI Category Logic
      const matchesBMI = filters.bmiCategory === 'all' || getBMICategory(patient.bmi) === filters.bmiCategory;

      return matchesSearch && matchesRegion && matchesSmoker && matchesAge && matchesBMI && matchesTreatment && matchesStatus;
    });
  }, [filters]);

  return {
    filters,
    setFilters,
    updateFilter,
    resetFilters,
    filteredData,
  };
};

/**
 * Main hook for healthcare data operations including KPIs and simulated loading states
 */
export const useHealthcareData = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { filters, updateFilter, resetFilters, filteredData } = usePatientFilters();

  // Simulate data fetching delay for a more realistic UI experience
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Recalculate KPIs dynamically based on the filtered data set
  const dynamicKPIs = useMemo((): KPIMetric[] => {
    if (filteredData.length === 0) {
      return [
        { title: 'Total Patients', value: 0, change: 0, trend: 'neutral', icon: 'Users', description: 'No matches found', color: 'oklch(0.52 0.18 255)' },
        { title: 'Avg. Treatment Cost', value: '$0', change: 0, trend: 'neutral', icon: 'DollarSign', description: 'N/A', color: 'oklch(0.68 0.16 165)' },
        { title: 'Critical Cases', value: 0, change: 0, trend: 'neutral', icon: 'Activity', description: 'N/A', color: 'oklch(0.55 0.18 28)' },
        { title: 'Recovery Rate', value: '0%', change: 0, trend: 'neutral', icon: 'HeartPulse', description: 'N/A', color: 'oklch(0.65 0.15 190)' }
      ];
    }

    const totalCharges = filteredData.reduce((acc, p) => acc + p.charges, 0);
    const avgCharges = totalCharges / filteredData.length;
    const criticalCount = filteredData.filter(p => p.status === 'Critical').length;
    const recoveryCount = filteredData.filter(p => p.status === 'Recovering').length;
    const recoveryRate = (recoveryCount / filteredData.length) * 100;

    return [
      {
        title: 'Total Patients',
        value: filteredData.length,
        change: 12,
        trend: 'up',
        icon: 'Users',
        description: 'Active patients in view',
        color: 'oklch(0.52 0.18 255)'
      },
      {
        title: 'Avg. Treatment Cost',
        value: formatCurrency(avgCharges),
        change: -5.2,
        trend: 'down',
        icon: 'DollarSign',
        description: 'Mean expenditure for current set',
        color: 'oklch(0.68 0.16 165)'
      },
      {
        title: 'Critical Cases',
        value: criticalCount,
        change: 8.4,
        trend: 'up',
        icon: 'Activity',
        description: 'High-risk cases in selection',
        color: 'oklch(0.55 0.18 28)'
      },
      {
        title: 'Recovery Rate',
        value: `${recoveryRate.toFixed(1)}%`,
        change: 2.1,
        trend: 'up',
        icon: 'HeartPulse',
        description: 'Selection outcome success',
        color: 'oklch(0.65 0.15 190)'
      }
    ];
  }, [filteredData]);

  // Calculate regional distribution for the current filtered data
  const dynamicRegionalStats = useMemo(() => {
    const regions = ['northeast', 'northwest', 'southeast', 'southwest'];
    return regions.map(reg => {
      const set = filteredData.filter(p => p.region === reg);
      const charges = set.reduce((acc, p) => acc + p.charges, 0);
      return {
        name: reg.charAt(0).toUpperCase() + reg.slice(1),
        patients: set.length,
        charges: Math.floor(charges),
        avgRisk: set.length > 0 ? Math.floor(set.reduce((acc, p) => acc + p.riskScore, 0) / set.length) : 0
      };
    });
  }, [filteredData]);

  return {
    isLoading,
    error,
    data: filteredData,
    allPatients: patientData,
    kpis: dynamicKPIs,
    regionalStats: dynamicRegionalStats,
    filters,
    updateFilter,
    resetFilters,
    setError
  };
};